import { ReportSelectionContext } from '@/contextProviders/ReportSelection';
import { KText } from '@/ui-library/components';
import { Box, Flex } from '@chakra-ui/react';
import { COLORS } from '@kula-ai/design-tokens';
import { useContext } from 'react';
import { CardTileHeading } from '../CardTileHeading';

const npsScoreConfig = {
  survey_candidate_nps_card: {
    topLabel: 'Candidate NPS',
    metricDefinition: 'The aggregate NPS from candidate surveys',
    basedOn: 'Submission date',
    category: 'Survey',
  },
  survey_hm_nps_card: {
    topLabel: 'HM NPS',
    metricDefinition: 'The aggregate NPS from candidate surveys',
    basedOn: 'Submission date',
    category: 'Survey',
  },
};

interface ReportNpsScoreProps {
  data: {
    score: number;
    promoters: number;
    passives: number;
    detractors: number;
  };
  selectConfig?: {
    isChecked: boolean;
    onChange: (checked: boolean) => void;
  };
  containerProps?: Record<string, any>;
  isSelect?: boolean;
  isFullScreen?: boolean;
  onCardClick?: (e: React.MouseEvent) => void;
  tooltipProps?: any;
  onDrillDownOpen?: (data: any) => void;
  isStaticReport?: boolean;
  onTileMetaChange?: (tileId: string, metadata: any) => void;
  tileData?: any;
  currentTitle?: string;
  isPrintView?: boolean;
  hasDrilldown?: boolean;
  /** Display labels for applied filters not supported by this block */
  unsupportedFilterLabels?: string[];
}

const Item = ({
  item,
  data,
  style = {},
  onDrillDownOpen,
  surveyType,
  hasDrilldown = true,
}) => {
  const { id, label, color } = item ?? {};
  const value = data?.[id] ?? 0;
  const type = surveyType === 'survey_candidate_nps_card' ? 'Candidate' : 'HM';

  return (
    <Flex
      key={id}
      w="100%"
      p="10px 6px"
      alignItems="center"
      justifyContent="space-between"
      {...style}
      onClick={(e) => {
        if (hasDrilldown) {
          e.stopPropagation();
          onDrillDownOpen?.({
            metric: `${type} ${label}`,
            type: 'chart',
            metricValue: `${value}%`,
            xAxis: label,
            yAxis: `${type} NPS`,
          });
        }
      }}
      cursor={hasDrilldown ? 'pointer' : 'default'}
      {...(hasDrilldown && {
        _hover: {
          textDecoration: 'underline',
          color: 'primary-600',
        },
      })}
    >
      <Flex flexDir="row" alignItems="center" gap="6px">
        <Box w="10px" h="10px" bg={color} borderRadius="full" />
        <KText>{label}</KText>
      </Flex>
      {typeof value === 'number' && <KText variant="heading_4">{value}%</KText>}
    </Flex>
  );
};

const listItems = [
  {
    id: 'promoters',
    label: 'Promoters (9-10)',
    color: COLORS.status.green,
  },
  {
    id: 'passives',
    label: 'Passives (7-8)',
    color: COLORS.status.yellow,
  },
  {
    id: 'detractors',
    label: 'Detractors (0-6)',
    color: COLORS.status.red,
  },
];

export const ReportNpsScore: React.FC<ReportNpsScoreProps> = ({
  data,
  tileData,
  selectConfig,
  isPrintView,
  isFullScreen,
  onDrillDownOpen,
  isStaticReport,
  onTileMetaChange,
  currentTitle,
  hasDrilldown = true,
  unsupportedFilterLabels,
}) => {
  const { truncationLength } = useContext(ReportSelectionContext);

  const { score = 30 } = data ?? {};
  const size = 300;

  const values = [-100, -50, 0, 50, 100];
  const scaleFactor = size / 400; // Base size for scaling is 400

  // 1. Dynamic Sizing based on scaleFactor (Base 400px values)
  const baseStrokeWidth = 28;
  const baseOuterMargin = 60; // Space between arc and edge
  const baseInnerGap = 6; // Gap between arc and center circle
  const baseTickLengthMajor = 10;
  const baseTickLengthMinor = 5;
  const baseLabelOffset = 35; // Distance of labels from arc outer edge
  const baseNeedleArrowSize = 28;
  const baseNeedleArrowWidth = 28;
  const baseNeedleLengthAdjustment = 36;

  // Apply scale factor
  const strokeWidth = baseStrokeWidth * scaleFactor;
  const outerMargin = baseOuterMargin * scaleFactor;
  const innerGap = baseInnerGap * scaleFactor;
  const tickLengthMajor = baseTickLengthMajor * scaleFactor;
  const tickLengthMinor = baseTickLengthMinor * scaleFactor;
  const labelOffset = baseLabelOffset * scaleFactor;
  const needleArrowSize = baseNeedleArrowSize * scaleFactor;
  const needleArrowWidth = baseNeedleArrowWidth * scaleFactor;
  const needleLengthAdjustment = baseNeedleLengthAdjustment * scaleFactor;

  // 2. Dynamic Font Sizes (Base 400px values)
  const labelFontSize = Math.max(8, 16 * scaleFactor); // Min 8px
  const npsValueFontSize = Math.max(20, 72 * scaleFactor); // Min 20px
  const npsLabelFontSize = Math.max(8, 20 * scaleFactor); // Min 8px

  // 3. Core Geometry
  const arcSpan = 270;
  const startAngle = 135;
  const gapAngle = 1; // Gap remains fixed in degrees

  const cx = size / 2;
  const cy = size / 2;

  // Radius calculation now uses scaled values
  const radius = (size - strokeWidth) / 2 - outerMargin;
  const innerRadius = radius - strokeWidth / 2 - innerGap;
  const centerRingStroke = 3 * scaleFactor;

  const rad = (d) => (d * Math.PI) / 180;

  const polarToCartesian = (centerX, centerY, r, angleDeg) => {
    const a = rad(angleDeg);
    return {
      x: centerX + r * Math.cos(a),
      y: centerY + r * Math.sin(a),
    };
  };

  const angleForValue = (val) => {
    const t = (val - values[0]) / (values[values.length - 1] - values[0]);
    return startAngle + t * arcSpan;
  };

  const segments = [
    { start: -100, end: -50, color: COLORS.status.red },
    { start: -50, end: 0, color: COLORS.status.red },
    { start: 0, end: 50, color: COLORS.status.yellow },
    { start: 50, end: 100, color: COLORS.status.green },
  ];

  const createArcPath = (
    startVal,
    endVal,
    r,
    gapAtStart = false,
    gapAtEnd = false
  ) => {
    let startA = angleForValue(startVal);
    let endA = angleForValue(endVal);

    if (gapAtStart) startA += gapAngle / 2;
    if (gapAtEnd) endA -= gapAngle / 2;

    const start = polarToCartesian(cx, cy, r, startA);
    const end = polarToCartesian(cx, cy, r, endA);
    const largeArc = endA - startA > 180 ? 1 : 0;
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`;
  };

  // Needle calculations using scaled values
  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
  const needleVal = clamp(score, -100, 100);
  const needleAngle = angleForValue(needleVal);

  const needleLength = radius + strokeWidth / 2 - needleLengthAdjustment;
  const needlePt = polarToCartesian(cx, cy, needleLength, needleAngle);

  const perpAngle = needleAngle + 90;
  const arrowTip = polarToCartesian(
    cx,
    cy,
    needleLength + needleArrowSize,
    needleAngle
  );
  const arrowBase1 = polarToCartesian(
    needlePt.x,
    needlePt.y,
    needleArrowWidth / 2,
    perpAngle
  );
  const arrowBase2 = polarToCartesian(
    needlePt.x,
    needlePt.y,
    needleArrowWidth / 2,
    perpAngle + 180
  );

  const npsSvg = (
    <svg
      width={size - 30}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      xmlns="http://www.w3.org/2000/svg"
      aria-label="NPS Gauge"
      shapeRendering="geometricPrecision"
    >
      {/* Background circle track */}
      <path
        d={createArcPath(-100, 100, radius)}
        fill="none"
        stroke="#fff"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
      />

      {/* Colored segments */}
      {segments.map((seg, i) => {
        const gapAtStart = i > 0;
        const gapAtEnd = i < segments.length - 1;
        return (
          <path
            key={i}
            d={createArcPath(seg.start, seg.end, radius, gapAtStart, gapAtEnd)}
            fill="none"
            stroke={seg.color}
            strokeWidth={strokeWidth}
            strokeLinecap="butt"
            filter="url(#shadow)"
            shapeRendering="geometricPrecision"
          />
        );
      })}

      {/* Minor tick marks - uses scaled lengths */}
      {(() => {
        const ticks = [];
        for (let val = -100; val <= 100; val += 10) {
          if (values.includes(val)) continue;
          const angle = angleForValue(val);
          const tickStart = polarToCartesian(
            cx,
            cy,
            radius + strokeWidth / 2 + 5 * scaleFactor, // Scale fixed offset (5)
            angle
          );
          const tickEnd = polarToCartesian(
            cx,
            cy,
            radius + strokeWidth / 2 + 5 * scaleFactor + tickLengthMinor, // Add scaled minor length
            angle
          );
          ticks.push(
            <line
              key={val}
              x1={tickStart.x}
              y1={tickStart.y}
              x2={tickEnd.x}
              y2={tickEnd.y}
              stroke="#9ca3af"
              strokeWidth={1.5 * scaleFactor} // Scale stroke
              strokeLinecap="round"
            />
          );
        }
        return ticks;
      })()}

      {/* Major tick marks - uses scaled lengths */}
      {values.map((val, i) => {
        const angle = angleForValue(val);
        const tickStart = polarToCartesian(
          cx,
          cy,
          radius + strokeWidth / 2 + 5 * scaleFactor, // Scale fixed offset (5)
          angle
        );
        const tickEnd = polarToCartesian(
          cx,
          cy,
          radius + strokeWidth / 2 + 5 * scaleFactor + tickLengthMajor, // Add scaled major length
          angle
        );

        return (
          <line
            key={`tick-${i}`}
            x1={tickStart.x}
            y1={tickStart.y}
            x2={tickEnd.x}
            y2={tickEnd.y}
            stroke="#374151"
            strokeWidth={2 * scaleFactor} // Scale stroke
            strokeLinecap="round"
          />
        );
      })}

      {/* Center circle with white background - uses scaled stroke */}
      <circle
        cx={cx}
        cy={cy}
        r={innerRadius}
        fill="white"
        stroke="#D9D9D9"
        strokeWidth={centerRingStroke}
        shapeRendering="geometricPrecision"
      />

      {/* NPS Value text - uses dynamic font size */}
      <text
        x={cx}
        y={cy - npsValueFontSize * 0.15} // Adjusted Y based on size
        fontSize={'40px'} //need to confirm with product team
        fontWeight="650"
        fontFamily="system-ui, -apple-system, sans-serif"
        textAnchor="middle"
        dominantBaseline="middle"
        fill="#413B74"
        onClick={() => {
          if (hasDrilldown) {
            onDrillDownOpen?.({
              metric: topLabel,
              type: 'metrics',
              metricValue: score,
              tableHeader: topLabel,
            });
          }
        }}
        cursor={hasDrilldown ? 'pointer' : 'default'}
        onMouseOver={(e) => {
          if (hasDrilldown) {
            (e.target as SVGTextElement).style.textDecoration = 'underline';
          }
        }}
        onMouseOut={(e) => {
          if (hasDrilldown) {
            (e.target as SVGTextElement).style.textDecoration = 'none';
          }
        }}
      >
        {score}
      </text>

      {/* NPS Score label - uses dynamic font size */}
      <text
        x={cx}
        y={cy + npsValueFontSize * 0.4} // Adjusted Y based on size
        fontSize={npsLabelFontSize}
        fontWeight="450"
        fontFamily="system-ui, -apple-system, sans-serif"
        textAnchor="middle"
        dominantBaseline="middle"
        fill="#6D659D"
      >
        NPS
      </text>

      {/* Needle Lines */}
      <line
        x1={arrowBase1.x}
        y1={arrowBase1.y}
        x2={arrowTip.x}
        y2={arrowTip.y}
        stroke="#D9D9D9"
        strokeWidth={5 * scaleFactor} // Scale stroke
        strokeLinecap="butt"
      />

      <line
        x1={arrowBase2.x}
        y1={arrowBase2.y}
        x2={arrowTip.x}
        y2={arrowTip.y}
        stroke="#D9D9D9"
        strokeWidth={5 * scaleFactor} // Scale stroke
        strokeLinecap="butt"
      />

      {/* Needle arrow */}
      <path
        d={`M ${arrowBase1.x} ${arrowBase1.y} L ${arrowTip.x} ${arrowTip.y} L ${arrowBase2.x} ${arrowBase2.y} Z`}
        fill="#fff"
      />

      {/* Labels - uses scaled offset and font size */}
      {values.map((val, i) => {
        const angle = angleForValue(val);
        const labelPos = polarToCartesian(
          cx,
          cy,
          radius + strokeWidth / 2 + labelOffset, // Uses scaled offset
          angle
        );

        return (
          <text
            key={`label-${i}`}
            x={labelPos.x}
            y={labelPos.y}
            fontSize={labelFontSize}
            fontWeight="600"
            fontFamily="system-ui, -apple-system, sans-serif"
            textAnchor="middle"
            dominantBaseline="middle"
            fill="#374151"
          >
            {val}
          </text>
        );
      })}
    </svg>
  );

  const { topLabel, metricDefinition, basedOn, category } =
    (npsScoreConfig[tileData?.metadata?.tileKey] ||
      npsScoreConfig[tileData?.api?.metadata?.tileKey]) ??
    {};

  return (
    <Flex
      p="10px"
      pt="0"
      w="100%"
      h="100%"
      flexDir="column"
      alignItems="center"
      justifyContent="center"
    >
      <>
        <CardTileHeading
          tileId={tileData?.id}
          checkboxId={tileData?.id}
          label={tileData?.metadata?.title ?? topLabel}
          metricDefinition={isFullScreen ? undefined : metricDefinition}
          basedOn={basedOn}
          category={category}
          selectConfig={selectConfig}
          truncationLength={truncationLength || 0}
          isStaticReport={isStaticReport}
          onTileMetaChange={onTileMetaChange}
          currentTitle={currentTitle}
          containerStyles={{
            width: '100%',
          }}
          unsupportedFilterLabels={unsupportedFilterLabels}
          isPrintView={isPrintView}
        />
        {npsSvg}
        <Item
          item={listItems[0]}
          data={data}
          onDrillDownOpen={onDrillDownOpen}
          surveyType={tileData?.metadata?.tileKey}
          hasDrilldown={hasDrilldown}
        />
        <Item
          item={listItems[1]}
          data={data}
          style={{
            borderTop: '1px solid',
            borderBottom: '1px solid',
            borderColor: 'border-color',
          }}
          onDrillDownOpen={onDrillDownOpen}
          surveyType={tileData?.metadata?.tileKey}
          hasDrilldown={hasDrilldown}
        />
        <Item
          item={listItems[2]}
          data={data}
          onDrillDownOpen={onDrillDownOpen}
          surveyType={tileData?.metadata?.tileKey}
          hasDrilldown={hasDrilldown}
        />
      </>
    </Flex>
  );
};
